import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Bonus, Freespin } from '@/api/common';
import type { GameV1 } from '@/api/games';
import { amountWithCurrencyCode, DEFAULT_CURRENCIES } from '@/helpers/currency';
import { formatDateTime } from '@/helpers/dateTime';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useCMSCashierBonuses } from '@/modules/bonuses';
import { useCMSPromocodes } from '@/modules/common/hooks/useCMSPromocodes';
import { getMinDepositAmount } from '@/modules/deposit/helpers';
import {
  getBonusStatusNameTranslation,
  isBonus,
} from '@/modules/promotions/components/PromotionsPage/helpers';

const formatDate = formatDateTime('dd.MM.yyyy / HH:mm:ss');

export const useFormatBonusTerms = (
  bonus: Bonus | Freespin,
  bonusGames?: GameV1[]
) => {
  const { t, i18n } = useTranslation('bonuses');

  const { data: promocodes } = useCMSPromocodes();
  const { bonuses: cashierBonuses } = useCMSCashierBonuses();

  const {
    validUntil,
    currency,
    activatable,
    activatableUntil,
    createdAt,
    stage,
    groupKey,
  } = bonus;

  const matchingBonusByGroupKey = useMemo(
    () =>
      cashierBonuses.find((p) => p.groupKey === groupKey) ??
      promocodes.find((p) => p.groupKey === groupKey),
    [cashierBonuses, promocodes, groupKey]
  );

  const {
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    freespinsTotal,
    maxWin,
  } = useMemo(
    () =>
      isBonus(bonus)
        ? {
            amountCents: bonus.amountCents,
            amountWagerCents: bonus.amountWagerCents,
            amountWagerRequirementCents: bonus.amountWagerRequirementCents,
            maxWin: bonus.maxWin,
          }
        : {
            freespinsTotal: bonus.freespinsTotal,
            gamesInfo: bonus.gamesInfo,
          },
    [bonus]
  );

  const bonusFormatedData = useMemo(() => {
    const amountWagerData =
      amountWagerCents !== undefined
        ? getCurrencyCentsAndCode(
            amountWagerCents,
            currency,
            DEFAULT_CURRENCIES
          )
        : undefined;

    const wagerRequirementData =
      amountWagerRequirementCents !== undefined
        ? getCurrencyCentsAndCode(
            amountWagerRequirementCents,
            currency,
            DEFAULT_CURRENCIES
          )
        : undefined;

    const bonusAmountData =
      amountCents !== undefined
        ? getCurrencyCentsAndCode(amountCents, currency, DEFAULT_CURRENCIES)
        : undefined;

    const maxWinData =
      maxWin != null
        ? getCurrencyCentsAndCode(maxWin, currency, DEFAULT_CURRENCIES)
        : undefined;

    const status = getBonusStatusNameTranslation(
      t,
      i18n,
      stage,
      true
    ) as string;

    return {
      dateReceived: formatDate(createdAt),
      activateUntilDate:
        activatable && activatableUntil
          ? formatDate(activatableUntil)
          : undefined,
      expireDate:
        !activatable && validUntil ? formatDate(validUntil) : undefined,
      wager:
        amountWagerRequirementCents && amountCents
          ? `${amountWagerRequirementCents / amountCents}x`
          : undefined,
      amountWager: amountWagerData
        ? amountWithCurrencyCode(
            amountWagerData.currency,
            amountWagerData.amount,
            true
          )
        : undefined,
      wagerRequirement: wagerRequirementData
        ? amountWithCurrencyCode(
            wagerRequirementData.currency,
            wagerRequirementData.amount
          )
        : undefined,
      maxWin: maxWinData
        ? amountWithCurrencyCode(maxWinData.currency, maxWinData.amount, true)
        : undefined,
      bonusAmount: bonusAmountData
        ? amountWithCurrencyCode(
            bonusAmountData.currency,
            bonusAmountData.amount
          )
        : undefined,
      bonusFreespins: freespinsTotal ? `${freespinsTotal}x` : undefined,
      spinValue: (() => {
        const amount = getMinDepositAmount(
          matchingBonusByGroupKey?.spinValue,
          currency
        );

        return amount
          ? amountWithCurrencyCode(currency, amount, true)
          : undefined;
      })(),
      bonusGamesLinks: bonusGames?.length ? (
        <>
          {bonusGames.map(({ title, fullPath }, i) => (
            <a href={`game/${fullPath}`} key={i}>
              {title}
              {i !== bonusGames.length - 1 ? ', ' : ''}
            </a>
          ))}
        </>
      ) : (
        ''
      ),
      status,
    };
  }, [
    activatable,
    activatableUntil,
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    createdAt,
    currency,
    freespinsTotal,
    bonusGames,
    i18n,
    matchingBonusByGroupKey,
    maxWin,
    stage,
    t,
    validUntil,
  ]);

  return bonusFormatedData;
};
